import {
  BasePagination,
  Checkbox,
  formatPrice,
  Heading,
  HStack,
  IconButtonWithTooltip,
  PlusIcon,
  VStack,
  WhiteCard,
} from '@elkaso-app/web-design';
import { useGetRestaurantByIdApi } from 'apis/restaurants/use-get-restaurant-by-id-api';
import { useGetWarehouseByIdApi } from 'apis/restaurants/use-get-warehouse-by-id-api';
import { useGetCatalogItemUnitsApi } from 'apis/restaurants/warehouse-items/use-get-catalog-item-units';
import { useGetCatalogItemsApi } from 'apis/restaurants/warehouse-items/use-get-catalog-items-api';
import { BaseTable, BaseTableContainer, useCustomTableCheckboxes } from 'components/base-table';
import {
  BaseTableSpinner,
  BaseTableTitle,
  ClearFilterButton,
  CustomTableCell,
  TableCell,
  TableHeader,
} from 'components/base-table/components';
import { ActivationFilterButton, NameFilterButton } from 'components/base-table/filters';
import { PriceFilterButton } from 'components/base-table/filters/price';
import { SkuFilterButton } from 'components/base-table/filters/sku';
import { UnitIdFilterButton } from 'components/base-table/filters/unit-id';
import { BaseSortButton } from 'components/base-table/sorting';
import { setHiddenColumns } from 'components/base-table/utils';
import { Can } from 'configs/can';
import { useSetPaginationParamsV1 } from 'hooks/use-set-pagination-params-v1';
import { IProductCategoryCell, ProductCategoryCell } from 'pages/restaurants/components/product-category-cell';
import { IProductImageCell, ProductImageCell } from 'pages/restaurants/components/product-image-cell';
import { AddItemModalCell } from 'pages/restaurants/components/warehouse-items/add-item-modal';
import { DownloadCSVDemoFile } from 'pages/restaurants/components/warehouse-items/download-csv-demo-file';
import { DownloadProductCSVFile } from 'pages/restaurants/components/warehouse-items/download-product-csv-file';
import { IItemStatusCell, ItemStatusCell } from 'pages/restaurants/components/warehouse-items/item-status-cell';
import {
  IProductListActionsCell,
  ProductListActionsCell,
} from 'pages/restaurants/components/warehouse-items/product-actions-cell';
import { RemoveItemsButton } from 'pages/restaurants/components/warehouse-items/remove-items-button';
import { UploadCSVFiles } from 'pages/restaurants/components/warehouse-items/upload-csv-files';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { CellProps } from 'react-table';

export const WarehouseProductsScreen = () => {
  const setPaginationParams = useSetPaginationParamsV1();

  const { restaurantId, warehouseId } = useParams<{ restaurantId: string; warehouseId: string }>();

  // Get warehouse details
  const { data: warehouseData } = useGetWarehouseByIdApi(restaurantId, warehouseId);

  // Get restaurant details
  const { data: restaurantData } = useGetRestaurantByIdApi(restaurantId);

  // Get warehouse items
  const { isLoading, isFetching, data } = useGetCatalogItemsApi(restaurantId, warehouseId);
  const { pageCount, total, data: itemsData } = data ?? {};

  // Get Units *Used in units filter & edit/add new item modal
  useGetCatalogItemUnitsApi();

  // Handle select table rows
  const { checkedItems, isIndeterminate, allChecked, setCheckboxState, handleParentChange, handleChildChange } =
    useCustomTableCheckboxes(itemsData);

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader name='Image' />,
        accessor: 'image',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['image']>) => (
          <CustomTableCell<IProductImageCell> as={ProductImageCell} imageURL={value} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Name (EN)'
            sortButtonComponent={<BaseSortButton sortBy='name' />}
            filterButtonComponent={<NameFilterButton />}
          />
        ),
        accessor: 'nameEn',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['nameEn']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Name (AR)' />,
        accessor: 'nameAr',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['nameAr']>) => <TableCell value={value} />,
      },
      {
        Header: (
          <TableHeader
            name='SKU'
            sortButtonComponent={<BaseSortButton sortBy='sku' />}
            filterButtonComponent={<SkuFilterButton />}
          />
        ),
        accessor: 'sku',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['sku']>) => <TableCell value={value} />,
      },
      {
        Header: (
          <TableHeader
            name='Price'
            sortButtonComponent={<BaseSortButton sortBy='price' />}
            filterButtonComponent={<PriceFilterButton />}
          />
        ),
        accessor: 'price',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['price']>) => <TableCell value={formatPrice(value)} />,
      },
      {
        Header: <TableHeader name='Unit' filterButtonComponent={<UnitIdFilterButton />} />,
        accessor: 'unit',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['unit']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Description' />,
        accessor: 'description',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['description']>) => <TableCell value={value} />,
      },
      {
        Header: <TableHeader name='Categories' />,
        accessor: 'categories',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['categories']>) => (
          <CustomTableCell<IProductCategoryCell> as={ProductCategoryCell} categoryName={value} />
        ),
      },
      {
        Header: (
          <TableHeader
            name='Status'
            sortButtonComponent={<BaseSortButton sortBy='isActive' />}
            filterButtonComponent={<ActivationFilterButton />}
          />
        ),
        accessor: 'status',
        Cell: ({ value }: CellProps<any, IColumnsAccessorData['status']>) => (
          <CustomTableCell<IItemStatusCell> as={ItemStatusCell} itemId={value?.itemId} isActive={value?.isActive} />
        ),
      },
      {
        Header: <TableHeader name='Actions' />,
        accessor: 'actions',
        Cell: ({ row, value }: CellProps<any, IColumnsAccessorData['actions']>) => (
          <CustomTableCell<IProductListActionsCell>
            as={ProductListActionsCell}
            row={row}
            item={value}
            isSelected={checkedItems[row.index]?.isChecked}
            onSelectChange={handleChildChange}
          />
        ),
      },
    ],
    [checkedItems]
  );

  interface IColumnsAccessorData {
    id: number;
    image: string;
    nameAr: string;
    nameEn: string;
    sku: string;
    price: number;
    unit: string;
    description: string;
    categories: string;
    status: {
      itemId: string;
      isActive: boolean;
    };
    actions: { [key: string]: any };
  }

  const columnsAccessorData = useMemo(
    () =>
      itemsData?.map((item: (typeof itemsData)[0]): IColumnsAccessorData => {
        return {
          id: item?.id,
          image: item?.images?.[0],
          nameAr: item?.name?.ar,
          nameEn: item?.name?.en,
          sku: item?.sku,
          price: item?.price,
          unit: item?.unit?.abbreviation,
          description: item?.description,
          categories: item?.category?.tree?.find((item: any) => item.depth === 1)?.title,
          status: {
            itemId: item?.id,
            isActive: item?.isActive,
          },
          actions: item,
        };
      }),
    [itemsData]
  );

  return (
    <VStack spacing='sm' align='stretch'>
      <WhiteCard p='md'>
        <VStack spacing='md' align='stretch'>
          <Heading as='h3' fontSize='lg'>
            Upload Warehouse Products From CSV File
          </Heading>

          <DownloadCSVDemoFile />
          <UploadCSVFiles />
        </VStack>
      </WhiteCard>

      <BaseTableContainer isLoading={isLoading}>
        <BaseTableContainer.Header>
          <BaseTableContainer.Header.Left>
            <VStack spacing='sm' align='start'>
              <HStack>
                <BaseTableTitle title={`${restaurantData?.data?.name} > ${warehouseData?.name?.en}`} />

                <BaseTableSpinner isFetching={isFetching} />
              </HStack>

              <DownloadProductCSVFile
                warehouseName={warehouseData?.name?.en}
                warehouseId={warehouseData?.id}
                restaurantName={restaurantData?.data?.name}
                restaurantId={restaurantData?.data?.id}
              />
            </VStack>
          </BaseTableContainer.Header.Left>

          <BaseTableContainer.Header.Right>
            <ClearFilterButton />

            <Can I='update' a='User'>
              <AddItemModalCell
                variant='add'
                trigger={<IconButtonWithTooltip label='Add item' aria-label='add-item-btn' Icon={<PlusIcon />} />}
              />
            </Can>

            <Can I='update' a='User'>
              <RemoveItemsButton checkedItems={checkedItems} />
            </Can>

            <Can I='update' a='User'>
              <IconButtonWithTooltip
                label='Select all'
                aria-label='select-btn'
                isDisabled={!itemsData?.length}
                onClick={() => {
                  handleParentChange(!allChecked);
                  setCheckboxState(!allChecked);
                }}
                Icon={
                  <Checkbox
                    mb='unset'
                    colorScheme='gray'
                    borderColor='#757575'
                    isChecked={allChecked}
                    isIndeterminate={isIndeterminate}
                    pointerEvents='none'
                  />
                }
              />
            </Can>
          </BaseTableContainer.Header.Right>
        </BaseTableContainer.Header>

        <BaseTableContainer.TableWrapper>
          <BaseTable
            columns={columns}
            data={columnsAccessorData}
            initialHiddenColumns={setHiddenColumns(['actions'])}
          />
        </BaseTableContainer.TableWrapper>

        <BaseTableContainer.Footer>
          <BaseTableContainer.Footer.Left />
          <BaseTableContainer.Footer.Right>
            <BasePagination>
              <BasePagination.Pages pagesCount={pageCount} setSearchParams={setPaginationParams} />
              <BasePagination.Limit total={total} setSearchParams={setPaginationParams} />
            </BasePagination>
          </BaseTableContainer.Footer.Right>
        </BaseTableContainer.Footer>
      </BaseTableContainer>
    </VStack>
  );
};
